import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

// ─────────────────────────────────────────────────────────────────────────────
// MONTAJE · el juego del portal
// Apila las piezas del escenario. Lo que sobresale se corta. Si clavas la pieza
// (PERFECTO) sumas combo y, a partir de x3, la pieza recupera ancho.
// Puntuación = pisos. Ranking semanal en arcade_scores con game = 'montaje'.
// ─────────────────────────────────────────────────────────────────────────────

const GAME_ID = 'montaje';
const W = 400;
const H = 600;
const BH = 24;                 // alto de cada pieza
const BASE_W = 190;            // ancho inicial
const GROUND_Y = H - 70;       // línea del suelo en pantalla (sin cámara)
const PERFECT_TOL = 5;         // px de margen para PERFECTO
const GROW = 10;               // ancho recuperado por PERFECTO a partir de x3
const ACCENT = '#FFB800';
const BG = '#0f1117';
const RETRY_DELAY = 650;       // ms antes de poder reintentar tras caer
const SOUND_KEY = 'montaje_sound';

const PHASES = [
  'SUELO TÉCNICO', 'TARIMA', 'ESCENARIO', 'PANTALLAS LED', 'TRUSS',
  'ILUMINACIÓN', 'PIROTECNIA', 'CIELO DE VALENCIA', 'ESTRATOSFERA', 'ÓRBITA', 'LEYENDA',
];

const RIVAL_COLORS = ['#60a5fa', '#f472b6', '#34d399', '#a78bfa', '#fb923c',
                      '#22d3ee', '#f87171', '#a3e635', '#e879f9', '#fbbf24'];

const PENTA = [0, 2, 4, 7, 9];

// ── Audio (WebAudio, sin ficheros) ──────────────────────────────────────────
function createAudio() {
  let ctx = null;
  return {
    enabled: true,
    unlock() {
      try {
        if (!ctx) {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (AC) ctx = new AC();
        }
        if (ctx && ctx.state === 'suspended') ctx.resume();
      } catch { /* sin audio */ }
    },
    tone(freq, dur = 0.12, type = 'sine', vol = 0.14, slideTo = 0, delay = 0) {
      if (!this.enabled || !ctx) return;
      try {
        const t = ctx.currentTime + delay;
        const o = ctx.createOscillator();
        const gn = ctx.createGain();
        o.type = type;
        o.frequency.setValueAtTime(freq, t);
        if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
        gn.gain.setValueAtTime(vol, t);
        gn.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        o.connect(gn);
        gn.connect(ctx.destination);
        o.start(t);
        o.stop(t + dur + 0.03);
      } catch { /* sin audio */ }
    },
  };
}

function comboFreq(n) {
  const i = Math.max(0, n - 1);
  const oct = Math.min(Math.floor(i / 5), 2);
  return 523.25 * Math.pow(2, (PENTA[i % 5] + 12 * oct) / 12);
}

function vibrate(ms) {
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch { /* nada */ }
}

function readSoundPref() {
  try { return localStorage.getItem(SOUND_KEY) !== 'off'; } catch { return true; }
}

function blockColor(hue, l = 56) {
  return `hsl(${hue}, 68%, ${l}%)`;
}

function hueFor(level) {
  return (205 + level * 9) % 360;
}

// ── Estado de partida ───────────────────────────────────────────────────────
function newGame(rivals, myBest) {
  const g = {
    state: 'playing',
    blocks: [{ x: (W - BASE_W) / 2, w: BASE_W, hue: hueFor(0) }],
    moving: null,
    debris: [],
    particles: [],
    popups: [],
    floors: 0,
    combo: 0,
    maxCombo: 0,
    perfects: 0,
    cam: 0,
    zoom: 1,
    shake: 0,
    flash: 0,
    phase: 0,
    phaseFlash: 0,
    rivals,
    myBest,
    bestBeaten: false,
    deadAt: 0,
    result: null,
  };
  spawnMoving(g);
  return g;
}

function spawnMoving(g) {
  const top = g.blocks[g.blocks.length - 1];
  const level = g.blocks.length;
  const w = top.w;
  const minX = -w * 0.35;
  const maxX = W - w * 0.65;
  const fromLeft = level % 2 === 1;
  g.moving = {
    x: fromLeft ? minX : maxX,
    w,
    dir: fromLeft ? 1 : -1,
    speed: Math.min(165 + g.floors * 6.5, 540),
    minX,
    maxX,
    hue: hueFor(level),
  };
}

function burst(g, x, y, hue, n, power = 1) {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const v = (60 + Math.random() * 220) * power;
    g.particles.push({
      x, y,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v + 120 * power,
      life: 0.6 + Math.random() * 0.5,
      max: 1.1,
      size: 2 + Math.random() * 3,
      color: typeof hue === 'string' ? hue : `hsl(${hue}, 85%, ${60 + Math.random() * 20}%)`,
    });
  }
}

function popup(g, text, opts = {}) {
  g.popups.push({
    text,
    y: opts.y ?? null,          // null = centro de pantalla
    color: opts.color || '#ffffff',
    size: opts.size || 20,
    life: opts.life || 1.1,
    max: opts.life || 1.1,
    perfect: !!opts.perfect,
  });
}

// Coloca la pieza que se mueve. Devuelve false si se cae el montaje.
function place(g, audio) {
  const m = g.moving;
  const top = g.blocks[g.blocks.length - 1];
  const level = g.blocks.length;
  const dx = m.x - top.x;
  const yTop = (level + 1) * BH;

  if (Math.abs(dx) <= PERFECT_TOL) {
    g.combo += 1;
    g.perfects += 1;
    g.maxCombo = Math.max(g.maxCombo, g.combo);
    let w = top.w;
    let x = top.x;
    if (g.combo >= 3 && w < BASE_W) {
      const nw = Math.min(BASE_W, w + GROW);
      x -= (nw - w) / 2;
      w = nw;
    }
    g.blocks.push({ x, w, hue: m.hue, perfect: 1 });
    burst(g, x, yTop - BH / 2, m.hue, 10 + Math.min(g.combo, 10) * 2);
    burst(g, x + w, yTop - BH / 2, m.hue, 10 + Math.min(g.combo, 10) * 2);
    g.particles.push({ ring: true, x, w, y: yTop, life: 0.45, max: 0.45 });
    g.popups = g.popups.filter((p) => !p.perfect);
    popup(g, g.combo > 1 ? `PERFECTO x${g.combo}` : 'PERFECTO', {
      color: ACCENT, size: 18 + Math.min(g.combo, 8) * 2, perfect: true,
    });
    if (g.combo === 3) popup(g, 'La pieza crece', { color: '#ffffff', size: 13, y: 0.62 });
    audio.tone(comboFreq(g.combo), 0.18, 'triangle', 0.16);
    if (g.combo >= 5) audio.tone(comboFreq(g.combo) * 2, 0.12, 'sine', 0.06, 0, 0.05);
    g.shake = Math.max(g.shake, 2 + Math.min(g.combo, 10) * 0.7);
    g.flash = Math.min(0.35, 0.1 + g.combo * 0.03);
    vibrate(g.combo >= 5 ? 35 : 20);
  } else {
    const overlap = m.w - Math.abs(dx);
    if (overlap <= 0) {
      g.debris.push({ x: m.x, y: yTop, w: m.w, vx: m.dir * 90, vy: 0, rot: 0, vr: m.dir * 2, hue: m.hue });
      return false;
    }
    g.combo = 0;
    const nx = dx > 0 ? m.x : top.x;
    const cutX = dx > 0 ? m.x + overlap : m.x;
    const side = dx > 0 ? 1 : -1;
    g.debris.push({ x: cutX, y: yTop, w: Math.abs(dx), vx: side * 70, vy: 30, rot: 0, vr: side * (1.5 + Math.random() * 2), hue: m.hue });
    g.blocks.push({ x: nx, w: overlap, hue: m.hue, perfect: 0 });
    if (overlap < m.w * 0.22) {
      popup(g, '¡Por los pelos!', { color: '#f87171', size: 18 });
      audio.tone(220, 0.15, 'square', 0.05);
    }
    audio.tone(190, 0.12, 'sine', 0.18, 85);
    g.shake = Math.max(g.shake, 2);
    vibrate(10);
  }

  g.floors = g.blocks.length - 1;

  // Fase nueva cada 10 pisos
  const ph = Math.min(Math.floor(g.floors / 10), PHASES.length - 1);
  if (ph > g.phase) {
    g.phase = ph;
    g.phaseFlash = 1.6;
    [0, 4, 7, 12].forEach((s, i) => audio.tone(392 * Math.pow(2, s / 12), 0.16, 'triangle', 0.1, 0, i * 0.07));
  }

  // Rivales adelantados
  g.rivals.forEach((r) => {
    if (!r.beaten && g.floors > r.score) {
      r.beaten = true;
      r.flash = 1;
      burst(g, W / 2, (r.score + 1) * BH, r.color, 26, 1.2);
      popup(g, `Adelantas a ${r.name}`, { color: r.color, size: 20, y: 0.3, life: 1.4 });
      [0, 7, 12].forEach((s, i) => audio.tone(660 * Math.pow(2, s / 12), 0.12, 'square', 0.05, 0, i * 0.06));
    }
  });

  // Récord semanal propio
  if (!g.bestBeaten && g.myBest > 0 && g.floors > g.myBest) {
    g.bestBeaten = true;
    burst(g, W / 2, (g.myBest + 1) * BH, ACCENT, 40, 1.4);
    popup(g, 'NUEVO RÉCORD', { color: ACCENT, size: 28, y: 0.24, life: 1.6 });
  }

  spawnMoving(g);
  return true;
}

function update(g, dt, now) {
  if (g.state === 'playing' && g.moving) {
    const m = g.moving;
    m.x += m.dir * m.speed * dt;
    if (m.x > m.maxX) { m.x = m.maxX; m.dir = -1; }
    if (m.x < m.minX) { m.x = m.minX; m.dir = 1; }
  }

  // Cámara: la cima de la torre a ~42% de la pantalla
  const level = g.blocks.length;
  let camTarget = Math.max(0, (level + 1) * BH - (GROUND_Y - H * 0.42));
  let zoomTarget = 1;
  if (g.state === 'dead' && now - g.deadAt > 450) {
    const towerH = level * BH + 30;
    zoomTarget = Math.min(1, (GROUND_Y - 300) / towerH);
    camTarget = 0;
  }
  const k = Math.min(1, dt * (g.state === 'dead' ? 3 : 7));
  g.cam += (camTarget - g.cam) * k;
  g.zoom += (zoomTarget - g.zoom) * k;

  g.debris.forEach((d) => {
    d.vy -= 1500 * dt;
    d.x += d.vx * dt;
    d.y += d.vy * dt;
    d.rot += d.vr * dt;
  });
  g.debris = g.debris.filter((d) => d.y > g.cam - 900);

  g.particles.forEach((p) => {
    p.life -= dt;
    if (!p.ring) {
      p.vy -= 600 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
  });
  g.particles = g.particles.filter((p) => p.life > 0);

  g.popups.forEach((p) => { p.life -= dt; });
  g.popups = g.popups.filter((p) => p.life > 0);

  g.rivals.forEach((r) => { if (r.flash > 0) r.flash = Math.max(0, r.flash - dt * 1.5); });
  g.blocks.forEach((b) => { if (b.perfect > 0) b.perfect = Math.max(0, b.perfect - dt * 3); });

  g.shake = Math.max(0, g.shake - dt * 30);
  g.flash = Math.max(0, g.flash - dt * 1.4);
  g.phaseFlash = Math.max(0, g.phaseFlash - dt);
}

// ── Dibujo ──────────────────────────────────────────────────────────────────
function makeStars() {
  return Array.from({ length: 140 }, () => ({
    x: Math.random() * W,
    y: Math.random() * 2400,
    s: Math.random() * 1.6 + 0.4,
    tw: Math.random() * Math.PI * 2,
  }));
}

function drawBlock(ctx, x, y, w, h, hue, glow) {
  ctx.fillStyle = blockColor(hue);
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = blockColor(hue, 72);
  ctx.fillRect(x, y, w, Math.max(1, h * 0.16));
  ctx.fillStyle = blockColor(hue, 40);
  ctx.fillRect(x, y + h - Math.max(1, h * 0.16), w, Math.max(1, h * 0.16));
  if (glow > 0) {
    ctx.fillStyle = `rgba(255,255,255,${0.55 * glow})`;
    ctx.fillRect(x, y, w, h);
  }
}

function render(ctx, g, stars, now, view) {
  const floors = g ? g.floors : 0;
  const cam = g ? g.cam : 0;
  const z = g ? g.zoom : 1;

  // Fondo: cambia de tono con la altura
  const hb = (230 + floors * 2.2) % 360;
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, `hsl(${hb}, 45%, ${Math.max(4, 11 - floors * 0.08)}%)`);
  grad.addColorStop(1, `hsl(${(hb + 35) % 360}, 40%, 15%)`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Estrellas: aparecen al subir
  const starAlpha = Math.min(1, 0.15 + floors / 30);
  stars.forEach((s) => {
    const sy = ((s.y + cam * 0.25) % 2400) - 1800;
    if (sy < -5 || sy > H) return;
    const a = starAlpha * (0.55 + 0.45 * Math.sin(now / 600 + s.tw));
    ctx.fillStyle = `rgba(255,255,255,${a.toFixed(3)})`;
    ctx.fillRect(s.x, sy, s.s, s.s);
  });

  ctx.save();
  if (g && g.shake > 0) {
    ctx.translate((Math.random() - 0.5) * g.shake, (Math.random() - 0.5) * g.shake);
  }

  const toX = (x) => W / 2 + (x - W / 2) * z;
  const toY = (y) => GROUND_Y - (y - cam) * z;

  // Suelo
  const gy = toY(0);
  if (gy < H + 10) {
    ctx.fillStyle = '#0b0d13';
    ctx.fillRect(0, gy, W, H - gy + 20);
    ctx.fillStyle = 'rgba(255,184,0,0.5)';
    ctx.fillRect(0, gy, W, 2);
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    ctx.font = 'bold 10px -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('FERIA VALENCIA · ZONA DE MONTAJE', W / 2, gy + 26);
  }

  if (!g) {
    const bx = (W - BASE_W) / 2;
    ctx.fillStyle = '#2a2f3d';
    ctx.fillRect(bx, toY(BH), BASE_W, BH);
    ctx.fillStyle = 'rgba(255,184,0,0.8)';
    ctx.fillRect(bx, toY(BH), BASE_W, 3);
  }

  if (g) {
    // Líneas de rivales y récord propio
    const lines = g.rivals.map((r) => ({ ...r, mine: false }));
    if (g.myBest > 0) lines.push({ name: 'TU RÉCORD', score: g.myBest, color: ACCENT, beaten: g.bestBeaten, flash: 0, mine: true });
    lines.forEach((r) => {
      const ly = toY((r.score + 1) * BH);
      if (ly < -20 || ly > H + 20) return;
      ctx.globalAlpha = r.beaten ? 0.25 + r.flash * 0.75 : 0.9;
      ctx.strokeStyle = r.color;
      ctx.lineWidth = r.mine ? 2 : 1.5;
      ctx.setLineDash([6, 5]);
      ctx.beginPath();
      ctx.moveTo(0, ly);
      ctx.lineTo(W, ly);
      ctx.stroke();
      ctx.setLineDash([]);
      const label = `${r.name} · ${r.score}`;
      ctx.font = 'bold 10px -apple-system, sans-serif';
      const tw = ctx.measureText(label).width + 12;
      ctx.fillStyle = r.color;
      ctx.fillRect(W - tw - 6, ly - 16, tw, 15);
      ctx.fillStyle = BG;
      ctx.textAlign = 'left';
      ctx.fillText(label, W - tw, ly - 5);
      ctx.globalAlpha = 1;
    });

    // Torre
    g.blocks.forEach((b, i) => {
      const y1 = toY((i + 1) * BH);
      const hh = BH * z;
      if (y1 > H + 5 || y1 + hh < -5) return;
      if (i === 0) {
        ctx.fillStyle = '#2a2f3d';
        ctx.fillRect(toX(b.x), y1, b.w * z, hh);
        ctx.fillStyle = 'rgba(255,184,0,0.8)';
        ctx.fillRect(toX(b.x), y1, b.w * z, Math.max(1, 3 * z));
        if (z > 0.6) {
          ctx.fillStyle = 'rgba(255,255,255,0.35)';
          ctx.font = `bold ${Math.round(10 * z)}px -apple-system, sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText('OWN', toX(b.x + b.w / 2), y1 + hh * 0.68);
        }
        return;
      }
      drawBlock(ctx, toX(b.x), y1, b.w * z, hh, b.hue, b.perfect);
    });

    // Pieza en movimiento
    if (g.state === 'playing' && g.moving) {
      const m = g.moving;
      const level = g.blocks.length;
      const y1 = toY((level + 1) * BH);
      ctx.shadowColor = blockColor(m.hue, 60);
      ctx.shadowBlur = 14;
      drawBlock(ctx, toX(m.x), y1, m.w * z, BH * z, m.hue, 0);
      ctx.shadowBlur = 0;
    }

    // Trozos que caen
    g.debris.forEach((d) => {
      ctx.save();
      ctx.translate(toX(d.x + d.w / 2), toY(d.y - BH / 2));
      ctx.rotate(d.rot);
      drawBlock(ctx, -d.w * z / 2, -BH * z / 2, d.w * z, BH * z, d.hue, 0);
      ctx.restore();
    });

    // Partículas y anillos
    g.particles.forEach((p) => {
      const a = Math.max(0, p.life / p.max);
      if (p.ring) {
        const grow = (1 - a) * 18;
        ctx.strokeStyle = `rgba(255,184,0,${a})`;
        ctx.lineWidth = 2;
        ctx.strokeRect(toX(p.x) - grow, toY(p.y) - grow, p.w * z + grow * 2, BH * z + grow * 2);
        return;
      }
      ctx.globalAlpha = a;
      ctx.fillStyle = p.color;
      ctx.fillRect(toX(p.x), toY(p.y), p.size, p.size);
      ctx.globalAlpha = 1;
    });
  }
  ctx.restore();

  // Destello de PERFECTO
  if (g && g.flash > 0) {
    ctx.fillStyle = `rgba(255,184,0,${g.flash * 0.35})`;
    ctx.fillRect(0, 0, W, H);
  }

  drawHud(ctx, g, now, view);
}

function nextRival(g) {
  const ahead = g.rivals.filter((r) => !r.beaten).sort((a, b) => a.score - b.score);
  return ahead[0] || null;
}

function drawHud(ctx, g, now, view) {
  ctx.textAlign = 'center';

  if (!g) {
    // Pantalla de inicio
    ctx.fillStyle = 'rgba(15,17,23,0.55)';
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = ACCENT;
    ctx.font = 'bold 44px -apple-system, sans-serif';
    ctx.fillText('MONTAJE', W / 2, 190);
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.font = '15px -apple-system, sans-serif';
    ctx.fillText('Apila el escenario pieza a pieza.', W / 2, 228);
    ctx.fillText('Lo que sobresale, se corta.', W / 2, 250);
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.font = '13px -apple-system, sans-serif';
    ctx.fillText('Clava 3 seguidas y la pieza crece.', W / 2, 280);
    if (view.champion) {
      ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.font = '12px -apple-system, sans-serif';
      ctx.fillText(`Campeón de la semana pasada: ${view.champion.winner} (${view.champion.score})`, W / 2, 330);
    }
    if (view.leader) {
      ctx.fillStyle = ACCENT;
      ctx.font = 'bold 13px -apple-system, sans-serif';
      ctx.fillText(`Líder esta semana: ${view.leader.name} · ${view.leader.score} pisos`, W / 2, 352);
    }
    const at = view.attempts;
    if (at && at.remaining <= 0) {
      ctx.fillStyle = '#f87171';
      ctx.font = 'bold 15px -apple-system, sans-serif';
      ctx.fillText(`Has usado tus ${at.limit} intentos de hoy`, W / 2, 420);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '13px -apple-system, sans-serif';
      ctx.fillText('Vuelve mañana', W / 2, 442);
      return;
    }
    const pulse = 0.55 + 0.45 * Math.sin(now / 280);
    ctx.fillStyle = `rgba(255,255,255,${pulse})`;
    ctx.font = 'bold 15px -apple-system, sans-serif';
    ctx.fillText('Toca o pulsa espacio para empezar', W / 2, 420);
    if (at) {
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '12px -apple-system, sans-serif';
      ctx.fillText(`Intentos hoy: ${at.remaining} de ${at.limit}`, W / 2, 442);
    }
    return;
  }

  // Marcador
  if (g.state === 'playing') {
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 56px -apple-system, sans-serif';
    ctx.fillText(String(g.floors), W / 2, 78);
    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = 'bold 10px -apple-system, sans-serif';
    ctx.fillText('PISOS', W / 2, 94);

    ctx.textAlign = 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = 'bold 10px -apple-system, sans-serif';
    ctx.fillText(PHASES[g.phase], 14, 22);
  }

  if (g.combo >= 2 && g.state === 'playing') {
    ctx.textAlign = 'right';
    ctx.fillStyle = ACCENT;
    ctx.font = `bold ${14 + Math.min(g.combo, 10)}px -apple-system, sans-serif`;
    ctx.fillText(`COMBO x${g.combo}`, W - 14, 24);
  }

  if (g.state === 'playing') {
    const nr = nextRival(g);
    ctx.textAlign = 'center';
    ctx.font = '12px -apple-system, sans-serif';
    if (nr) {
      const left = nr.score - g.floors + 1;
      ctx.fillStyle = nr.color;
      ctx.fillText(`Siguiente: ${nr.name} · te faltan ${left}`, W / 2, 116);
    } else if (g.rivals.length > 0) {
      ctx.fillStyle = ACCENT;
      ctx.fillText('Vas primero esta semana', W / 2, 116);
    }
  }

  // Cambio de fase
  if (g.phaseFlash > 0) {
    const a = Math.min(1, g.phaseFlash);
    ctx.textAlign = 'center';
    ctx.fillStyle = `rgba(255,255,255,${a * 0.5})`;
    ctx.font = 'bold 11px -apple-system, sans-serif';
    ctx.fillText(`PISO ${g.phase * 10} · NUEVA FASE`, W / 2, H * 0.5 - 22);
    ctx.fillStyle = `rgba(255,184,0,${a})`;
    ctx.font = 'bold 26px -apple-system, sans-serif';
    ctx.fillText(PHASES[g.phase], W / 2, H * 0.5 + 6);
  }

  // Textos flotantes
  let stack = 0;
  g.popups.forEach((p) => {
    const a = Math.min(1, p.life / p.max * 1.6);
    const rise = (1 - p.life / p.max) * 30;
    const baseY = p.y !== null ? H * p.y : H * 0.36 + stack * 26;
    if (p.y === null) stack += 1;
    ctx.textAlign = 'center';
    ctx.globalAlpha = a;
    ctx.fillStyle = p.color;
    ctx.font = `bold ${p.size}px -apple-system, sans-serif`;
    ctx.fillText(p.text, W / 2, baseY - rise);
    ctx.globalAlpha = 1;
  });

  // Fin de partida
  if (g.state === 'dead' && now - g.deadAt > RETRY_DELAY) {
    ctx.fillStyle = 'rgba(15,17,23,0.78)';
    ctx.fillRect(16, 18, W - 32, 236);
    ctx.textAlign = 'center';
    ctx.fillStyle = '#f87171';
    ctx.font = 'bold 12px -apple-system, sans-serif';
    ctx.fillText('SE HA CAÍDO EL MONTAJE', W / 2, 46);
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 44px -apple-system, sans-serif';
    ctx.fillText(`${g.floors} pisos`, W / 2, 94);
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.font = '12px -apple-system, sans-serif';
    ctx.fillText(`${PHASES[g.phase]} · ${g.perfects} perfectos · combo máx. x${g.maxCombo}`, W / 2, 118);

    const r = g.result;
    ctx.font = 'bold 14px -apple-system, sans-serif';
    if (!r) {
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.fillText('Guardando...', W / 2, 150);
    } else if (r.error) {
      ctx.fillStyle = '#f87171';
      ctx.fillText('No se ha podido guardar la puntuación', W / 2, 150);
    } else {
      ctx.fillStyle = r.saved ? ACCENT : 'rgba(255,255,255,0.65)';
      ctx.fillText(r.saved ? 'Nuevo récord semanal' : `Tu récord esta semana: ${r.weeklyBest}`, W / 2, 150);
      if (r.position) {
        ctx.fillStyle = '#ffffff';
        ctx.font = '13px -apple-system, sans-serif';
        ctx.fillText(`Vas ${r.position}º de ${r.total} esta semana`, W / 2, 174);
      }
      if (r.gap) {
        ctx.fillStyle = 'rgba(255,255,255,0.55)';
        ctx.font = '12px -apple-system, sans-serif';
        ctx.fillText(`A ${r.gap.diff} ${r.gap.diff === 1 ? 'piso' : 'pisos'} de ${r.gap.name}`, W / 2, 194);
      }
    }
    const at = view.attempts;
    if (at && at.remaining <= 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.6)';
      ctx.font = 'bold 14px -apple-system, sans-serif';
      ctx.fillText('No te quedan intentos hoy. Vuelve mañana', W / 2, 232);
    } else {
      const pulse = 0.55 + 0.45 * Math.sin(now / 250);
      ctx.fillStyle = `rgba(255,184,0,${pulse})`;
      ctx.font = 'bold 15px -apple-system, sans-serif';
      const left = at ? ` (te ${at.remaining === 1 ? 'queda 1 intento' : `quedan ${at.remaining} intentos`})` : '';
      ctx.fillText(`Toca para volver a montar${left}`, W / 2, 232);
    }
  }
}

// ── Componente ──────────────────────────────────────────────────────────────
export default function Arcade() {
  const canvasRef = useRef(null);
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [history, setHistory] = useState([]);
  const [weeklyBest, setWeeklyBest] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [soundOn, setSoundOn] = useState(readSoundPref);
  const [attempts, setAttempts] = useState(null);

  const gameRef = useRef(null);
  const audioRef = useRef(null);
  const starsRef = useRef(null);
  const viewRef = useRef({ champion: null, leader: null, attempts: null });
  const startingRef = useRef(false);
  const lbRef = useRef({ leaderboard: [], weeklyBest: 0 });
  const userNameRef = useRef('');

  if (!audioRef.current) audioRef.current = createAudio();
  if (!starsRef.current) starsRef.current = makeStars();
  userNameRef.current = user?.name || '';
  audioRef.current.enabled = soundOn;

  const loadLeaderboard = useCallback(async () => {
    try {
      const [lb, hist] = await Promise.all([
        api.get(`/arcade/leaderboard/${GAME_ID}?limit=10`),
        api.get(`/arcade/history/${GAME_ID}?limit=4`).catch(() => ({ history: [] })),
      ]);
      const list = lb.leaderboard || [];
      setLeaderboard(list);
      setWeeklyBest(lb.personalBest || 0);
      setHistory(hist.history || []);
      lbRef.current = { leaderboard: list, weeklyBest: lb.personalBest || 0 };
      viewRef.current = {
        champion: (hist.history || [])[0] || null,
        leader: list[0] || null,
        attempts: lb.attempts || null,
      };
      setAttempts(lb.attempts || null);
      return list;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);

  const submitScore = useCallback(async (g) => {
    try {
      const res = await api.post('/arcade/scores', { game: GAME_ID, score: Math.round(g.floors), playId: g.playId });
      const list = (await loadLeaderboard()) || [];
      const me = userNameRef.current;
      const idx = list.findIndex((e) => e.name === me);
      let gap = null;
      if (idx > 0) {
        const above = list[idx - 1];
        gap = { name: above.name.split(' ')[0], diff: above.score - list[idx].score + 1 };
      }
      g.result = {
        saved: res.saved,
        weeklyBest: res.weeklyBest,
        position: idx >= 0 ? idx + 1 : null,
        total: list.length,
        gap,
      };
    } catch {
      g.result = { error: true };
    }
  }, [loadLeaderboard]);

  const submitRef = useRef(submitScore);
  submitRef.current = submitScore;

  const start = useCallback(async () => {
    const at = viewRef.current.attempts;
    if (startingRef.current || (at && at.remaining <= 0)) return;
    startingRef.current = true;
    let playId;
    try {
      const res = await api.post('/arcade/plays', { game: GAME_ID });
      playId = res.playId;
      viewRef.current = { ...viewRef.current, attempts: res.attempts };
      setAttempts(res.attempts);
    } catch {
      // Sin intentos o error: refresca el contador y vuelve a la pantalla de inicio
      gameRef.current = null;
      await loadLeaderboard();
      startingRef.current = false;
      return;
    }
    startingRef.current = false;
    const me = userNameRef.current;
    const rivals = lbRef.current.leaderboard
      .filter((e) => e.name !== me && e.score > 0)
      .sort((a, b) => a.score - b.score)
      .map((e, i) => ({
        name: e.name.split(' ')[0],
        score: e.score,
        color: RIVAL_COLORS[i % RIVAL_COLORS.length],
        beaten: false,
        flash: 0,
      }));
    const g = newGame(rivals, lbRef.current.weeklyBest);
    g.playId = playId;
    gameRef.current = g;
  }, [loadLeaderboard]);

  const press = useCallback(() => {
    const audio = audioRef.current;
    audio.unlock();
    const g = gameRef.current;
    const now = performance.now();
    if (!g) { start(); return; }
    if (g.state === 'playing') {
      const ok = place(g, audio);
      if (!ok) {
        g.state = 'dead';
        g.deadAt = now;
        g.shake = 9;
        g.combo = 0;
        audio.tone(330, 0.5, 'sawtooth', 0.08, 70);
        vibrate([40, 40, 60]);
        submitRef.current(g);
      }
      return;
    }
    if (g.state === 'dead' && now - g.deadAt > RETRY_DELAY) start();
  }, [start]);

  const pressRef = useRef(press);
  pressRef.current = press;

  // Bucle de juego
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext('2d');
    let raf = 0;
    let last = performance.now();
    const loop = (now) => {
      const dt = Math.min(0.033, (now - last) / 1000);
      last = now;
      const g = gameRef.current;
      if (g) update(g, dt, now);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      render(ctx, g, starsRef.current, now, viewRef.current);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Teclado
  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat) return;
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.code === 'Space' || e.code === 'Enter' || e.code === 'ArrowUp') {
        e.preventDefault();
        pressRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem(SOUND_KEY, next ? 'on' : 'off'); } catch { /* nada */ }
  };

  const medalColor = (i) => (i === 0 ? ACCENT : i === 1 ? '#c0c0c0' : i === 2 ? '#cd7f32' : 'rgba(255,255,255,0.3)');

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: BG }}>
      <div className="mx-auto w-full" style={{ maxWidth: 820 }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md flex items-center justify-center font-bold text-sm" style={{ backgroundColor: ACCENT, color: BG }}>E</div>
            <span className="text-white font-bold tracking-wide text-sm">MONTAJE</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={toggleSound} className="text-xs" style={{ color: soundOn ? ACCENT : 'rgba(255,255,255,0.4)' }}>
              {soundOn ? 'Sonido: sí' : 'Sonido: no'}
            </button>
            <a href="/" className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Volver al portal</a>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-start">
          <div className="w-full md:w-auto mx-auto" style={{ maxWidth: W }}>
            <canvas
              ref={canvasRef}
              onPointerDown={(e) => { e.preventDefault(); press(); }}
              className="w-full rounded-xl cursor-pointer select-none"
              style={{ aspectRatio: `${W} / ${H}`, touchAction: 'manipulation', border: '1px solid rgba(255,255,255,0.06)' }}
            />
            <p className="text-center mt-2 text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
              Espacio, clic o toque para soltar la pieza
            </p>
          </div>

          <div className="w-full md:flex-1 rounded-xl overflow-hidden" style={{ backgroundColor: '#1a1d27' }}>
            <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {[['SEMANA ACTUAL', false], ['CAMPEONES', true]].map(([label, val]) => (
                <button
                  key={label}
                  onClick={() => setShowHistory(val)}
                  className="flex-1 py-2.5 text-xs font-medium"
                  style={{ color: showHistory === val ? ACCENT : 'rgba(255,255,255,0.4)', borderBottom: showHistory === val ? `2px solid ${ACCENT}` : '2px solid transparent' }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="p-4">
              {!showHistory ? (
                leaderboard.length > 0 ? (
                  <div className="space-y-2">
                    {leaderboard.map((entry, i) => (
                      <div key={entry.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-3">
                          <span className="w-5 text-right font-bold" style={{ color: medalColor(i) }}>{i + 1}</span>
                          <span className="text-white">{entry.name}</span>
                          {entry.name === user?.name && (
                            <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(255,184,0,0.15)', color: ACCENT }}>Tú</span>
                          )}
                        </div>
                        <span className="font-mono font-bold" style={{ color: ACCENT }}>{entry.score}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-3 text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Nadie ha montado nada esta semana. Estrena el ranking.</p>
                )
              ) : (
                history.length > 0 ? (
                  <div className="space-y-2">
                    {history.map((week) => {
                      const d = new Date(week.week_start);
                      return (
                        <div key={week.week_start} className="flex items-center justify-between text-sm">
                          <div>
                            <span className="text-white font-medium">{week.winner}</span>
                            <span className="block text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{`Semana del ${d.getUTCDate()}/${d.getUTCMonth() + 1}`}</span>
                          </div>
                          <span className="font-mono font-bold text-sm" style={{ color: ACCENT }}>{week.score}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center py-3 text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Aún no hay semanas cerradas.</p>
                )
              )}
            </div>

            <div className="px-4 pb-4 text-xs space-y-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {attempts && (
                <p>
                  Intentos hoy:{' '}
                  <span className="font-mono font-bold" style={{ color: attempts.remaining > 0 ? ACCENT : '#f87171' }}>
                    {attempts.remaining} de {attempts.limit}
                  </span>
                  {attempts.remaining <= 0 && ' · vuelve mañana'}
                </p>
              )}
              {weeklyBest > 0 && <p>Tu récord esta semana: <span className="font-mono font-bold" style={{ color: ACCENT }}>{weeklyBest}</span></p>}
              <p>La semana se cierra el viernes a las 18:00. Quien vaya primero a esa hora es el campeón y el bot lo anuncia en #general. Lo que se juegue el fin de semana cuenta para la semana siguiente. Tienes 2 intentos al día: cada partida que empiezas cuenta, aunque la dejes a medias.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
